import {
  DOCUMENT_STATUS,
  DOCUMENT_STATUS_PRECEDENCE,
} from '../config/brandDetailsConfig'

const cleanValue = (value) => `${value ?? ''}`.trim()

export const formatFileSize = (sizeInBytes = 0) => {
  if (sizeInBytes < 1024) return `${sizeInBytes} B`

  const sizeInKilobytes = sizeInBytes / 1024
  if (sizeInKilobytes < 1024) {
    return `${Math.round(sizeInKilobytes * 10) / 10} KB`
  }

  return `${Math.round((sizeInKilobytes / 1024) * 10) / 10} MB`
}

const getDocuments = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.documents)) return response.documents
  return []
}

// The document exists, so it has been uploaded — we just have no current
// version to read a workflow status from.
const getRawStatus = (document) => {
  const version =
    document.latest_document_version || document.latestDocumentVersion
  if (!version) return ''

  const isLatest = version.is_latest ?? version.isLatest
  if (isLatest === false) return ''

  return cleanValue(
    version.brand_dna_document_status || version.brandDnaDocumentStatus,
  ).toUpperCase()
}

const getDocumentStatus = (document) => {
  const status = getRawStatus(document)
  return DOCUMENT_STATUS_PRECEDENCE.includes(status)
    ? status
    : DOCUMENT_STATUS.UPLOADED
}

export const mapBrandDocuments = (response) =>
  getDocuments(response).map((document) => {
    const documentType = document.document_type || {}
    const version = document.latest_document_version || {}

    return {
      createdAt: cleanValue(document.created_at),
      documentTypeId: cleanValue(documentType.document_type_uuid),
      documentTypeLabel: cleanValue(documentType.display_name),
      documentTypeName: cleanValue(documentType.name),
      // The status exactly as the API reports it, for the Knowledge Base.
      brandDnaStatus: cleanValue(
        version.brand_dna_document_status,
      ).toUpperCase(),
      fileName: cleanValue(version.file_name),
      id: cleanValue(document.brand_document_uuid),
      sizeBytes: Number(version.size_bytes) || 0,
      status: getDocumentStatus(document),
      updatedAt: cleanValue(document.updated_at),
      // The latest version, as the Knowledge Base lists it.
      uploadedAt: cleanValue(version.created_at),
      uploadedBy: cleanValue(version.uploaded_by?.display_name),
      versionNumber: cleanValue(version.version_number),
    }
  })

const toRanks = (statuses = []) =>
  statuses
    .map((status) => DOCUMENT_STATUS_PRECEDENCE.indexOf(status))
    .filter((rank) => rank >= 0)

// Answers "how far has this type got at best?" — the upload rail's question,
// where one approved document is enough to call the type covered.
const getHighestStatus = (statuses = []) => {
  const ranks = toRanks(statuses)
  return ranks.length
    ? DOCUMENT_STATUS_PRECEDENCE[Math.min(...ranks)]
    : DOCUMENT_STATUS.NOT_UPLOADED
}

// Answers "how far has this type got at worst?" — the drafts rail's question.
// A type whose files are half extracted still reads as extracting, because the
// reviewer cannot act on it until every file has finished.
const getLowestStatus = (statuses = []) => {
  const ranks = toRanks(statuses)
  return ranks.length
    ? DOCUMENT_STATUS_PRECEDENCE[Math.max(...ranks)]
    : DOCUMENT_STATUS.NOT_UPLOADED
}

// Newest upload first, so the most recent version of a type reads at the top.
const byNewestFirst = (left, right) =>
  cleanValue(right.createdAt).localeCompare(cleanValue(left.createdAt))

const groupDocumentsByTypeId = (documents = []) =>
  documents.reduce((grouped, document) => {
    if (!document.documentTypeId) return grouped
    const typeDocuments = grouped.get(document.documentTypeId)
    if (typeDocuments) typeDocuments.push(document)
    else grouped.set(document.documentTypeId, [document])
    return grouped
  }, new Map())

// A document type holds every document uploaded against it — the API groups
// them by document_type_uuid, and one type commonly carries several files.
// `rollup` decides which of those files' statuses speaks for the type.
const mapTypeStatuses = (documentTypes, documents, rollup) => {
  const documentsByTypeId = groupDocumentsByTypeId(documents)

  return documentTypes.map((documentType) => {
    const typeDocuments = documentsByTypeId.get(documentType.id) || []

    return {
      ...documentType,
      documents: [...typeDocuments].sort(byNewestFirst),
      status: rollup(typeDocuments.map(({ status }) => status)),
    }
  })
}

export const mapDocumentTypeStatuses = (documentTypes = [], documents = []) =>
  mapTypeStatuses(documentTypes, documents, getHighestStatus)

export const mapExtractionTypeStatuses = (documentTypes = [], documents = []) =>
  mapTypeStatuses(documentTypes, documents, getLowestStatus)
